<!-- src/views/CompletedFormView.vue -->
<template>
  <div class="min-h-screen bg-[#F0F0F0] font-sans selection:bg-[#20FCF1]/30 text-left">
    
    <!-- MASTER CONTAINER (Synchronised with Dashboard Alignment) -->
    <div class="p-4 sm:p-6 md:p-8">
      <div class="max-w-7xl mx-auto pb-24 text-left">

        <!-- 1. PREMIUM HEADER (Screen Only) -->
        <div class="bg-[#0A1630] rounded-[40px] p-8 md:p-10 border-b-4 border-[#20FCF1] shadow-2xl animate-fadeIn relative overflow-hidden text-left border-x border-t border-slate-800 mb-10 no-print">
          <div class="absolute top-0 right-0 p-12 opacity-5">
            <ShieldCheckIcon class="h-32 w-32 text-white" />
          </div>
          
          <div class="relative z-10 flex flex-col xl:flex-row xl:items-center justify-between gap-8">
            <div class="flex items-center gap-6">
              <div v-if="qrCodeUrl" class="shrink-0 p-2 bg-white rounded-2xl shadow-xl border border-white/10 no-print">
                 <img :src="qrCodeUrl" alt="Audit QR" class="w-16 h-16 grayscale">
              </div>
              <div class="space-y-1">
                <h1 class="text-3xl font-black text-white uppercase tracking-tight leading-none">
                  Record: <span class="text-[#20FCF1]">{{ formReference }}</span>
                </h1>
                <div class="flex items-center gap-3 text-slate-400">
                  <p class="text-[9px] font-black uppercase tracking-[0.2em] text-[#20FCF1]">{{ formTitle }} Registry</p>
                  <span class="w-1 h-1 rounded-full bg-slate-700"></span>
                  <p class="text-[9px] font-black uppercase tracking-[0.2em]">Sentinel Safe Protocol</p>
                </div>
              </div>
            </div>
            
            <div class="flex flex-wrap items-center gap-3">
              <!-- 🛡️ INVESTIGATION NAVIGATION: SKIP TO SUMMARY -->
              <button 
                v-if="formIdentifier === 'hsInvestigationRegistry'"
                @click="skipToSummary"
                class="px-6 py-3.5 bg-[#0A1630] border border-[#20FCF1]/30 text-[#20FCF1] rounded-2xl font-black uppercase text-[10px] tracking-widest hover:bg-[#20FCF1] hover:text-[#0A1630] transition-all flex items-center gap-2 shadow-lg cursor-pointer"
              >
                <ArrowDownCircleIcon class="h-4 w-4" />
                <span>Skip to Summary</span>
              </button>

              <button 
                v-if="!isPdfGenerator"
                @click="exportToPdf" 
                class="bg-[#20FCF1] text-[#0A1630] px-6 py-3.5 rounded-2xl text-[10px] font-black uppercase tracking-widest hover:scale-105 transition-all flex items-center gap-2 shadow-xl cursor-pointer"
              >
                <DocumentArrowDownIcon class="h-4 w-4" />
                <span>Export PDF</span>
              </button>

              <button 
                v-if="isSupportRequest && userStore.isLoggedIn"
                @click="copySubmissionLink" 
                class="bg-white/5 border border-white/10 text-white px-6 py-3.5 rounded-2xl text-[10px] font-black uppercase tracking-widest hover:bg-white/10 transition-all flex items-center gap-2 shadow-lg cursor-pointer"
              >
                <DocumentDuplicateIcon class="w-4 h-4 text-[#20FCF1]" />
                {{ copyButtonText }}
              </button>

              <button v-if="userStore.isLoggedIn" @click="goBack" class="bg-white/5 border border-white/10 text-slate-400 px-6 py-3.5 rounded-2xl text-[10px] font-black uppercase tracking-widest hover:text-white transition-all shadow-lg cursor-pointer">
                {{ backLinkText }}
              </button>
            </div>
          </div>
        </div>

        <!-- 2. MAIN BODY -->
        <div v-if="!loading && submissionData" class="space-y-10 print:space-y-0 animate-fadeIn print:animate-none print:opacity-100 printable-container">
          
          <!-- STATUS UPDATE TOAST (Screen Only) -->
          <div v-if="showUpdateToast" class="fixed top-10 left-1/2 -translate-x-1/2 z-[100] bg-[#0A1630] border-b-4 border-[#20FCF1] text-white px-10 py-6 rounded-[32px] shadow-2xl flex items-center gap-6 animate-slideDown no-print">
            <div class="bg-[#20FCF1] p-2 rounded-full">
              <CheckCircleIcon class="h-6 w-6 text-[#0A1630]" />
            </div>
            <div>
              <p class="text-[10px] font-black uppercase tracking-widest text-[#20FCF1]">Registry Updated</p>
              <p class="text-sm font-bold uppercase">Status: {{ selectedStatus }} • Handshake Verified</p>
            </div>
          </div>

          <div class="bg-white shadow-2xl rounded-[40px] overflow-hidden border border-slate-100 text-left printable-sheet print:rounded-none print:border-none print:shadow-none print:opacity-100">
            
            <!-- 🛡️ REFINED PRINT HEADER -->
            <div class="hidden print:flex justify-between items-end border-b-2 border-[#0A1630] pb-4 mb-8">
               <div class="flex items-center gap-6">
                  <img v-if="clientLogoUrl" :src="clientLogoUrl" class="max-h-[55px] max-w-[150px] object-contain" alt="Client Logo">
                  <img v-else-if="submissionData.companyLogoUrl" :src="submissionData.companyLogoUrl" class="max-h-[55px] max-w-[150px] object-contain" alt="Client Logo">
                  <img v-else src="/images/tecs-logo.png" class="max-h-[55px] max-w-[150px] object-contain" alt="TECS Logo">
               </div>
               <div class="text-right flex items-center gap-6">
                  <div class="text-right">
                     <p class="text-[8px] font-black uppercase text-slate-400 tracking-[0.15em] mb-1">Official Compliance Record</p>
                     <h1 class="text-xl font-black uppercase text-[#0A1630] leading-none mb-1">{{ formTitle }} Registry Record</h1>
                     <p class="text-xs font-bold text-slate-700">REF: {{ formReference }}</p>
                  </div>
                  <img v-if="qrCodeUrl" :src="qrCodeUrl" class="w-16 h-16 border border-slate-100 p-1" />
               </div>
            </div>

            <!-- CORPORATE AUDIT HEADER (Visible on Screen Only) -->
            <div class="bg-slate-50/80 border-b border-slate-100 p-6 md:p-8 flex flex-col md:flex-row justify-between items-center gap-6 text-center md:text-left print:hidden">
              <div class="flex items-center gap-6">
                <div class="text-left">
                  <h2 class="text-xl font-black text-[#0A1630] uppercase tracking-tight leading-none mb-2">
                    {{ submissionData.companyDisplayName || 'Sentinel Safe Audit' }}
                  </h2>
                  <div class="flex flex-col md:flex-row md:items-center gap-3">
                    <span :class="statusClass(submissionData.status)" class="px-3 py-1 rounded-full text-[8px] font-black uppercase tracking-widest shadow-sm border border-black/5">
                      {{ submissionData.status === 'Completed' ? 'Reviewed' : (submissionData.status || 'Pending Review') }}
                    </span>
                    <span class="text-[9px] font-bold text-slate-400 uppercase tracking-widest font-sans leading-none">Personnel: {{ resolvedUserName }}</span>
                  </div>
                </div>
              </div>

              <div class="text-center md:text-right">
                <p class="text-[8px] font-black uppercase tracking-widest text-slate-400">Registry Timestamp</p>
                <!-- 🛡️ FIXED TOP TIMESTAMP: Falls back to signatureDate or createdAt if submittedAt is null -->
                <p class="text-[10px] font-bold text-[#0A1630] uppercase mt-1">{{ formatDate(submissionData.submittedAt || submissionData.signatureDate || submissionData.createdAt) }}</p>
              </div>
            </div>

            <!-- COMPONENT DISPATCHER -->
            <div class="p-6 md:p-10 print:p-0">
              <SupportRequestCompleted v-if="isSupportRequest" :submission-data="submissionData" :format-date="formatDate" />
              <div v-else>
                 <YourDetailsCompleted v-if="formIdentifier === 'yourDetails'" :submission-data="submissionData" :user-profile="userProfile" :format-date="formatDate" :format-text="formatText" />
                 <FatigueRiskAssessmentCompleted v-else-if="formIdentifier === 'fatigueRiskAssessment'" :submission-data="submissionData" :format-date="formatDate" :format-text="formatText" />
                 
                 <FatigueManagementPlanCompleted 
                   v-else-if="formIdentifier === 'fatigueManagementPlan'" 
                   :submission-data="submissionData" 
                   :format-date="formatDate" 
                   :format-text="formatText"
                   :is-target-personnel="isTargetOperative"
                   :profile-signature="userStore.userProfile?.signatureUrl || userStore.userProfile?.signatureDataUrl"
                   :is-updating="isUpdatingStatus"
                   @authorise="handleOperativeSignOff"
                 />

                 <FatigueAssessmentCompleted v-else-if="formIdentifier === 'fatigueAssessment'" :submission-data="submissionData" :format-date="formatDate" />
                 <RightToWorkCompleted v-else-if="formIdentifier === 'rightToWork'" :submission-data="submissionData" :user-profile="userProfile" :format-date="formatDate" :format-text="formatText" :get-file-icon="getFileIcon" :get-file-name-from-url="getFileNameFromUrl" :is-image="isImage" />
                 <InductionCompleted v-else-if="formIdentifier === 'employeeInduction'" :submission-data="submissionData" :format-date="formatDate" />
                 <RolesResponsibilitiesCompleted v-else-if="formIdentifier === 'rolesAndResponsibilities'" :submission-data="submissionData" :format-date="formatDate" />
                 <LiteracyTestCompleted v-else-if="formIdentifier === 'literacyTest'" :submission-data="submissionData" :format-date="formatDate" />
                 <PrimarySponsorContractCompleted v-else-if="formIdentifier === 'primarySponsorContract'" :submission-data="submissionData" :format-date="formatDate" :format-text="formatText" />
                 <AnnualHealthAssessmentCompleted v-else-if="formIdentifier === 'annualHealthAssessment'" :submission-data="submissionData" :format-date="formatDate" :format-yes-no-n-a="formatYesNoNA" :get-status-class="getStatusClass" />
                 <EyesightDeclarationCompleted v-else-if="formIdentifier === 'eyesightDeclaration'" :submission-data="submissionData" :format-date="formatDate" :format-yes-no-n-a="formatYesNoNA" :get-status-class="getStatusClass" />
                 <SubContractorPackCompleted v-else-if="formIdentifier === 'subContractorPack'" :submission-data="submissionData" :format-date="formatDate" :format-text="formatText" :format-yes-no-n-a="formatYesNoNA" :get-status-class="getStatusClass" :get-file-icon="getFileIcon" :get-file-name-from-url="getFileNameFromUrl" :is-image="isImage" />
                 <ObservationCompleted v-else-if="formIdentifier === 'observationForm'" :submission-data="submissionData" :format-date="formatDate" :format-text="formatText" :format-boolean="formatBoolean" />
                 <AccidentCompleted v-else-if="formIdentifier === 'accidentForm'" :submission-data="submissionData" :format-date="formatDate" :format-text="formatText" :format-boolean="formatBoolean" :get-file-icon="getFileIcon" :get-file-name-from-url="getFileNameFromUrl" />
                 <AccidentWitnessCompleted v-else-if="formIdentifier === 'accidentWitnessStatementForm'" :submission-data="submissionData" :format-date="formatDate" :format-text="formatText" :format-boolean="formatBoolean" />
                 <WorksiteInspectionCompleted v-else-if="formIdentifier === 'worksiteInspectionForm'" :submission-data="submissionData" :format-date="formatDate" :format-text="formatText" :format-yes-no-n-a="formatYesNoNA" :get-status-class="getStatusClass" :get-file-icon="getFileIcon" :get-file-name-from-url="getFileNameFromUrl" :is-image="isImage" />
                 <CossCompleted v-else-if="formIdentifier === 'cossCommunicationsMonitoring'" :submission-data="submissionData" :format-date="formatDate" :format-text="formatText" :format-yes-no-n-a="formatYesNoNA" :get-status-class="getStatusClass" />
                 <PpeRequestCompleted v-else-if="formIdentifier === 'ppeRequest'" :submission-data="submissionData" :format-date="formatDate" />
                 <EmployeeHandbookSignOffCompleted v-else-if="formIdentifier === 'employeeHandbookSignOff'" :submission-data="submissionData" :format-date="formatDate" />
                 <InvestigationCompleted v-else-if="formIdentifier === 'hsInvestigationRegistry'" :submission-data="submissionData" :format-date="formatDate" :format-text="formatText" :format-boolean="formatBoolean" />
                 <SingleItemInspectionCompleted v-else-if="formIdentifier === 'singleItemInspectionForm'" :submission-data="submissionData" :format-date="formatDate" />
                 <MultiItemInspectionCompleted v-else-if="formIdentifier === 'multiItemInspectionForm'" :submission-data="submissionData" :format-date="formatDate" />
                 
                 <!-- 🛡️ CLIENT FEEDBACK QUESTIONNAIRE COMPLETED SHEET -->
                 <ClientFeedbackCompleted v-else-if="formIdentifier === 'clientFeedbackForm'" :submission-data="submissionData" :format-date="formatDate" />

                 <!-- 🛡️ PERFORMANCE EVALUATION COMPLETED SHEET: Dynamic visual dispatcher renders nested evaluation card -->
                 <div v-else-if="formIdentifier === 'user_review'" class="space-y-8 text-left animate-fadeIn">
                   <div class="grid grid-cols-1 md:grid-cols-2 gap-6 text-left">
                     <div class="bg-slate-50 p-6 rounded-2xl border border-slate-100 shadow-inner">
                       <p class="text-slate-400 font-black uppercase text-[8px] tracking-widest mb-1.5 leading-none">Reviewer Identification</p>
                       <h4 class="font-black text-[#0A1630] text-sm uppercase leading-none">{{ submissionData.reviewerName || 'Site Manager' }}</h4>
                       <p class="text-[9px] text-slate-400 font-bold uppercase mt-2">Representing: {{ submissionData.company || 'N/A' }}</p>
                     </div>
                     <div class="bg-slate-50 p-6 rounded-2xl border border-slate-100 shadow-inner">
                       <p class="text-slate-400 font-black uppercase text-[8px] tracking-widest mb-1.5 leading-none">Project / Site Name</p>
                       <h4 class="font-black text-[#0A1630] text-sm uppercase leading-none">{{ submissionData.siteName || 'N/A' }}</h4>
                       <p class="text-[9px] text-slate-400 font-bold uppercase mt-2">Location ID: Registered</p>
                     </div>
                   </div>

                   <div class="space-y-4 text-left pt-4">
                     <p class="text-[9px] font-black text-slate-400 uppercase tracking-widest ml-1 leading-none">Performance Evaluation Metrics</p>
                     <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                       <div 
                         v-for="(val, metric) in (submissionData.metricBreakdown || {})" 
                         :key="metric"
                         class="bg-slate-50 p-4 rounded-2xl border border-slate-100 flex items-center justify-between"
                       >
                         <span class="text-[10px] font-black uppercase tracking-tight text-slate-600">{{ formatText(metric) }}</span>
                         <div class="flex items-center gap-1">
                           <StarIcon v-for="star in 5" :key="star" class="h-4 w-4" :class="star <= val ? 'text-amber-500 fill-amber-400' : 'text-slate-200'" />
                         </div>
                       </div>
                     </div>
                   </div>

                   <div class="bg-[#0A1630] p-6 rounded-[32px] text-white flex items-center justify-between border-l-4 border-l-[#20FCF1] shadow-xl">
                     <div class="flex flex-col text-left">
                       <span class="text-[9px] font-black uppercase text-[#20FCF1] tracking-widest">Aggregated Performance Score</span>
                       <div class="flex mt-1.5">
                         <StarIcon v-for="s in 5" :key="s" class="h-3 w-3" :class="s <= Math.round(submissionData.rating) ? 'text-amber-500 fill-amber-400' : 'text-white/10'" />
                       </div>
                     </div>
                     <span class="text-3xl font-black text-white italic tracking-tighter">{{ submissionData.rating ? parseFloat(submissionData.rating).toFixed(1) : '0.0' }}<span class="text-xs text-white/40 not-italic ml-1">/ 5.0</span></span>
                   </div>

                   <div class="space-y-2 text-left pt-4">
                     <p class="text-[9px] font-black text-slate-400 uppercase tracking-widest ml-1 leading-none">Evaluator Comments & Feedback Remarks</p>
                     <div class="bg-slate-50 p-6 rounded-[24px] border border-slate-100 text-slate-700 leading-relaxed italic text-xs shadow-inner">
                       "{{ submissionData.comments || 'No additional evaluation remarks registered.' }}"
                     </div>
                   </div>
                 </div>

                 <div v-else class="text-center py-24 bg-slate-50 rounded-[40px] border-2 border-dashed border-slate-200">
                    <DocumentTextIcon class="h-8 w-8 text-slate-300 mx-auto mb-4" />
                    <p class="text-[9px] font-black text-slate-400 uppercase tracking-widest">Unmapped Registry Document</p>
                    <p class="text-[8px] font-bold text-slate-400 mt-2">Identifier: {{ formIdentifier }}</p>
                 </div>
              </div>

              <!-- ADMINISTRATIVE AUDIT TRAIL BLOCK -->
              <div v-if="submissionData.statusChangedBy" class="mt-16 pt-8 border-t-2 border-slate-50 flex flex-col gap-6 animate-fadeIn text-left">
                 <div class="flex flex-col md:flex-row justify-between items-center gap-6">
                   <div class="flex items-center gap-4">
                      <div class="bg-[#0A1630] p-3 rounded-2xl border border-[#20FCF1]/30 shadow-lg">
                        <ShieldCheckIcon class="h-6 w-6 text-[#20FCF1]" />
                      </div>
                      <div>
                        <p class="text-[10px] font-black uppercase tracking-[0.2em] text-[#0A1630]">Administrative Verification Seal</p>
                        <p class="text-[9px] font-bold text-slate-400 uppercase mt-1">Permanent Registry Audit Log</p>
                      </div>
                   </div>
                   <div class="text-center md:text-right bg-slate-50 px-8 py-4 rounded-3xl border border-slate-100">
                      <p class="text-[10px] font-black text-[#0A1630] uppercase font-sans">Validated By: {{ submissionData.statusChangedBy }}</p>
                      <p class="text-[9px] font-bold text-[#20FCF1] uppercase tracking-widest mt-1">{{ formatDate(submissionData.statusChangedAt) }}</p>
                   </div>
                 </div>
                 
                 <!-- 🛡️ NEW: DISPLAY VERIFICATION AUDIT NOTES AT THE BASE OF THE DETAILED FORM -->
                 <div v-if="submissionData.statusNotes" class="bg-slate-50 p-5 rounded-2xl border border-slate-100">
                    <p class="text-[9px] font-black uppercase text-slate-400 tracking-widest mb-2 leading-none">Verification Audit Notes</p>
                    <p class="text-xs font-semibold text-slate-600 italic">"{{ submissionData.statusNotes }}"</p>
                 </div>
              </div>

              <!-- 🛡️ SECTION 2-5 COMMENTS & GOOD PRACTICES DISPLAY (Worksite Inspection) -->
              <div v-if="formIdentifier === 'worksiteInspectionForm' && (submissionData.safetyComments || submissionData.healthComments || submissionData.environmentalComments || submissionData.ssowComments)" class="mt-10 pt-8 border-t border-slate-100 space-y-6 text-left">
                 <div class="flex items-center gap-3">
                   <ShieldCheckIcon class="h-5 w-5 text-[#20FCF1]" />
                   <h3 class="text-sm font-black text-[#0A1630] uppercase tracking-wider">Section Comments & Good Practices</h3>
                 </div>
                 <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div v-if="submissionData.safetyComments" class="bg-slate-50 p-5 rounded-2xl border border-slate-100">
                       <p class="text-[8px] font-black text-slate-400 uppercase tracking-widest mb-1">Section 2 (Safety) Comments</p>
                       <p class="text-xs font-medium text-slate-700 italic">"{{ submissionData.safetyComments }}"</p>
                    </div>
                    <div v-if="submissionData.healthComments" class="bg-slate-50 p-5 rounded-2xl border border-slate-100">
                       <p class="text-[8px] font-black text-slate-400 uppercase tracking-widest mb-1">Section 3 (Health) Comments</p>
                       <p class="text-xs font-medium text-slate-700 italic">"{{ submissionData.healthComments }}"</p>
                    </div>
                    <div v-if="submissionData.environmentalComments" class="bg-slate-50 p-5 rounded-2xl border border-slate-100">
                       <p class="text-[8px] font-black text-slate-400 uppercase tracking-widest mb-1">Section 4 (Environmental) Comments</p>
                       <p class="text-xs font-medium text-slate-700 italic">"{{ submissionData.environmentalComments }}"</p>
                    </div>
                    <div v-if="submissionData.ssowComments" class="bg-slate-50 p-5 rounded-2xl border border-slate-100">
                       <p class="text-[8px] font-black text-slate-400 uppercase tracking-widest mb-1">Section 5 (SSoW) Comments</p>
                       <p class="text-xs font-medium text-slate-700 italic">"{{ submissionData.ssowComments }}"</p>
                    </div>
                 </div>
              </div>
              
              <!-- SUPPORT REQUEST Multi-Stage Logic -->
              <div v-if="isSupportRequest && userStore.isLoggedIn && canManageCurrentForm && !submissionData.internalResponse && !isPdfGenerator" class="mt-16 pt-16 border-t-4 border-slate-100 no-print text-left">
                  <div class="bg-white p-8 md:p-12 rounded-[40px] border-4 border-dashed border-slate-100 shadow-inner">
                      <h3 class="text-xl font-black text-[#0A1630] uppercase tracking-tight mb-8 leading-none">Process Request Authorisation</h3>
                      <div class="grid grid-cols-1 md:grid-cols-2 gap-8 text-left">
                          <div><label class="form-label">Lead Contact</label><input v-model="internalResponse.providerLead" type="text" class="form-input" placeholder="Authorising Lead"></div>
                          <div><label class="form-label">Reference / Quote No.</label><input v-model="internalResponse.quoteReference" type="text" class="form-input" placeholder="Registry Reference"></div>
                          <div class="md:col-span-2">
                            <label class="form-label">Upload Authorisation (PDF Only)</label>
                            <input type="file" @change="handleQuoteFileUpload" accept="application/pdf" class="block w-full text-xs text-slate-500 file:mr-4 file:py-3 file:px-6 file:rounded-xl file:border-0 file:text-[10px] file:font-black file:uppercase file:bg-[#0A1630] file:text-[#20FCF1] cursor-pointer">
                          </div>
                          <div class="md:col-span-2 pt-6"><button @click="submitInternalReply" :disabled="isSubmittingReply || !internalResponse.quoteReference" class="btn-premium">Submit Official Response</button></div>
                      </div>
                  </div>
              </div>

              <!-- Support Request Stage 3/4 -->
              <div v-if="isSupportRequest && submissionData.internalResponse && !submissionData.clientDecision && !isPdfGenerator" class="mt-16 pt-16 border-t-4 border-slate-100 animate-fadeIn text-left">
                  <div class="bg-[#0A1630] p-10 md:p-14 rounded-[40px] shadow-2xl text-white relative overflow-hidden">
                      <div class="absolute top-0 right-0 p-12 opacity-5"><ShieldCheckIcon class="h-32 w-32" /></div>
                      <div class="relative z-10">
                        <div class="flex flex-col sm:flex-row items-center gap-4 mb-10 text-center sm:text-left">
                          <div class="bg-[#20FCF1] text-[#0A1630] w-12 h-12 rounded-2xl flex items-center justify-center font-black">3</div>
                          <h3 class="text-2xl font-black uppercase tracking-tight text-white leading-none">Stage 3: Proposal Decision</h3>
                        </div>
                        <div class="space-y-8 text-left">
                           <textarea v-model="clientDecision.notes" rows="4" class="w-full bg-white/5 border border-white/10 rounded-[32px] p-6 text-white outline-none focus:ring-2 focus:ring-[#20FCF1] font-sans" placeholder="Decision Notes..."></textarea>
                           <div class="flex flex-col sm:flex-row gap-6">
                              <button @click="submitClientDecision('Authorised')" :disabled="isSubmittingDecision" class="flex-1 bg-[#20FCF1] text-[#0A1630] py-6 rounded-[24px] font-black uppercase text-sm shadow-xl hover:scale-[1.03] transition-all">GO (Accept Quote)</button>
                              <button @click="submitClientDecision('Declined')" :disabled="isSubmittingDecision" class="flex-1 bg-rose-50 text-white py-6 rounded-[24px] font-black uppercase text-sm shadow-xl hover:scale-[1.03] transition-all flex items-center justify-center gap-2">
                                 <XMarkIcon class="w-5 h-5" /> <span>ABORT (Decline)</span>
                              </button>
                           </div>
                        </div>
                      </div>
                  </div>
              </div>

              <div v-if="isSupportRequest && submissionData.clientDecision?.status === 'Authorised' && userStore.isLoggedIn && !submissionData.staffDeployment && !isPdfGenerator" class="mt-16 pt-16 border-t-4 border-slate-100 animate-fadeIn no-print text-left">
                  <div class="bg-slate-50 p-8 md:p-12 rounded-[40px] border-2 border-slate-100 shadow-inner">
                      <div class="flex items-center gap-4 mb-10 text-left">
                        <div class="bg-[#0A1630] text-[#20FCF1] w-12 h-12 rounded-2xl flex items-center justify-center font-black">4</div>
                        <h3 class="text-xl font-black uppercase text-[#0A1630] tracking-tight leading-none">Stage 4: Personnel Deployment</h3>
                      </div>
                      <div class="grid grid-cols-1 lg:grid-cols-3 gap-8 text-left font-sans">
                         <div v-for="idx in [1, 2, 3]" :key="idx">
                            <div v-if="submissionData['shiftStartDate' + idx]" class="bg-white rounded-[32px] border border-slate-200 overflow-hidden shadow-sm flex flex-col h-full">
                               <div class="bg-[#0A1630] p-5 border-b border-white/5"><h3 class="text-[10px] font-black text-[#20FCF1] uppercase tracking-[0.2em]">Deployment Shift {{ idx }}</h3></div>
                               <div class="p-6 space-y-6 flex-grow">
                                  <div class="bg-slate-50/50 p-4 rounded-2xl space-y-3 border border-slate-100 shadow-inner">
                                     <div class="flex justify-between"><p class="text-[8px] font-black text-slate-300 uppercase">Start</p><p class="text-[9px] font-bold text-slate-600">{{ formatDate(submissionData['shiftStartDate' + idx]) }}</p></div>
                                  </div>
                                  <div class="space-y-4 pt-2">
                                     <div><label class="form-label !text-[#20FCF1]">Assigned Staff Name</label><input v-model="staffDeployment['staffName' + idx]" type="text" class="form-input !bg-slate-50"></div>
                                  </div>
                               </div>
                            </div>
                         </div>
                      </div>
                      <button @click="submitStaffDeployment" :disabled="isSubmittingDeployment" class="w-full bg-[#0A1630] text-white py-6 rounded-[24px] font-black uppercase text-xs shadow-2xl mt-10 transition-all">Confirm Deployment & Registry Sync</button>
                  </div>
              </div>
            </div>

            <!-- 🛡️ TASK 1: PREMIUM SYSTEM FOOTER SIGNATURE (VISIBLE ONLY ON PRINTED PDF DOCUMENTATION) -->
            <div class="hidden print:flex justify-center items-center gap-2 border-t border-dashed border-slate-200 pt-6 mt-12 pb-6 opacity-55">
              <img src="/images/sentinel-safe-logo.png" class="h-5 w-5 object-contain" alt="Sentinel Safe Logo" />
              <!-- Using standard premium typography matched to our corporate dashboard styles -->
              <span class="font-sans font-black text-xs uppercase tracking-[0.15em] text-[#0A1630]">Sentinel Safe</span>
            </div>

          </div>

          <!-- GLOBAL REGISTRY STATUS CONTROL (Managers Only) -->
          <!-- 🛡️ UPDATED: Wired dropdown change and confirmation button to handle the interactive popup notes modal -->
          <section v-if="canManageCurrentForm && userStore.isLoggedIn && !isPdfGenerator" class="bg-[#0A1630] p-8 md:p-12 rounded-[40px] text-white shadow-2xl no-print animate-fadeIn text-left mt-10">
              <div class="flex flex-col md:flex-row items-center justify-between gap-10">
                <div class="space-y-3 text-center md:text-left">
                  <h3 class="text-2xl font-black uppercase tracking-tight text-[#20FCF1] leading-none">Registry Status Control</h3>
                  <p class="text-slate-400 text-sm font-medium font-sans">Update the operational status for this record.</p>
                </div>
                <div class="flex flex-col sm:flex-row items-center gap-6 bg-white/5 p-8 rounded-[32px] border border-white/10 w-full md:w-auto">
                  <div class="flex flex-col gap-1 flex-grow w-full text-left">
                      <label class="text-[9px] font-black text-[#20FCF1] uppercase tracking-widest ml-1">Target Registry Status</label>
                      <div class="flex items-center gap-3">
                        <ShieldCheckIcon class="h-5 w-5 text-[#20FCF1]" />
                        <select v-model="selectedStatus" @change="handleStatusSelectionChange" class="bg-slate-800 border-none text-white text-xs font-black uppercase rounded-xl px-5 py-3.5 outline-none focus:ring-2 focus:ring-[#20FCF1] min-w-[200px] cursor-pointer appearance-none font-sans">
                            <option v-for="status in statusOptions" :key="status" :value="status">{{ status }}</option>
                        </select>
                      </div>
                  </div>
                  <button @click="handleStatusUpdate" :disabled="isUpdatingStatus" class="w-full sm:w-auto bg-[#20FCF1] text-[#0A1630] px-12 py-4 rounded-2xl text-[10px] font-black uppercase shadow-xl hover:scale-105 active:scale-95 transition-all disabled:opacity-50">
                     <span v-if="isUpdatingStatus">Syncing...</span>
                     <span v-else>Update Protocol</span>
                  </button>
                </div>
              </div>
          </section>
        </div>
      </div>
    </div>

    <!-- 🛡️ NEW: NON-MANDATORY STATUS NOTES MODAL (Managers Only) -->
    <div v-if="showStatusNotesModal" class="fixed inset-0 z-[100] flex items-center justify-center bg-[#0A1630]/90 backdrop-blur-md px-4 animate-fadeIn no-print">
      <div class="bg-white rounded-[40px] shadow-2xl w-full max-w-lg p-10 border border-white/10 text-left animate-fadeIn">
        <div class="flex items-center gap-4 mb-8 text-left">
          <div class="bg-[#0A1630] text-[#20FCF1] w-12 h-12 rounded-2xl flex items-center justify-center shadow-lg">
            <ShieldCheckIcon class="h-6 w-6 text-[#20FCF1]" />
          </div>
          <h3 class="text-2xl font-black text-[#0A1630] uppercase tracking-tight text-left">
            Verify Registry Status
          </h3>
        </div>
        <div class="space-y-6">
          <div class="p-5 bg-slate-50 rounded-2xl border border-slate-100 text-left">
            <p class="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1 text-left">Target Status</p>
            <p class="text-sm font-bold text-[#0A1630] uppercase text-left">{{ selectedStatus }}</p>
          </div>
          <div class="text-left">
            <label class="block text-[10px] font-black text-[#0A1630] uppercase tracking-widest mb-3 ml-1">Audit Notes / Reason (Optional)</label>
            <textarea v-model="statusChangeNotes" rows="4" class="w-full border-slate-200 rounded-2xl p-5 text-sm font-bold bg-slate-50 outline-none focus:ring-2 focus:ring-[#20FCF1] transition-all shadow-inner placeholder-slate-400" placeholder="e.g. Completed form is no longer required due to project restructure..."></textarea>
          </div>
        </div>
        <div class="mt-10 flex gap-4">
          <button @click="closeStatusNotesModal" class="flex-1 py-5 text-[10px] font-black uppercase text-slate-400 text-center hover:text-[#0A1630] transition-colors">Abort</button>
          <button @click="confirmStatusUpdate" :disabled="isUpdatingStatus" class="flex-1 py-5 rounded-2xl text-[10px] font-black uppercase shadow-xl disabled:opacity-50 text-white bg-[#0A1630] hover:bg-[#20FCF1] hover:text-[#0A1630] transition-all">
            {{ isUpdatingStatus ? 'Synchronising...' : 'Confirm Update' }}
          </button>
        </div>
      </div>
    </div>

  </div>
</template>

<script setup>
import { ref, onMounted, watch, computed, reactive } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { db, auth } from '@/firebaseConfig';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { doc, getDoc, updateDoc, serverTimestamp, addDoc, collection } from 'firebase/firestore';
import { getStorage, ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';
import QRCode from 'qrcode';
import { useUserStore } from '@/stores/userStore';
import { 
  DocumentDuplicateIcon, ShieldCheckIcon, DocumentTextIcon, 
  DocumentArrowDownIcon, CheckCircleIcon,
  XMarkIcon, ArrowDownCircleIcon
} from '@heroicons/vue/24/outline';
import { StarIcon } from '@heroicons/vue/24/solid';

// CHILD COMPONENTS
import SupportRequestCompleted from '@/components/forms/completed/SupportRequestCompleted.vue';
import FatigueRiskAssessmentCompleted from '@/components/forms/completed/FatigueRiskAssessmentCompleted.vue';
import FatigueManagementPlanCompleted from '@/components/forms/completed/FatigueManagementPlanCompleted.vue';
import FatigueAssessmentCompleted from '@/components/forms/completed/FatigueAssessmentCompleted.vue';
import YourDetailsCompleted from '@/components/forms/completed/YourDetailsCompleted.vue';
import InductionCompleted from '@/components/forms/completed/InductionCompleted.vue';
import RolesResponsibilitiesCompleted from '@/components/forms/completed/RolesResponsibilitiesCompleted.vue';
import LiteracyTestCompleted from '@/components/forms/completed/LiteracyTestCompleted.vue';
import PrimarySponsorContractCompleted from '@/components/forms/completed/PrimarySponsorContractCompleted.vue';
import AnnualHealthAssessmentCompleted from '@/components/forms/completed/AnnualHealthAssessmentCompleted.vue';
import EyesightDeclarationCompleted from '@/components/forms/completed/EyesightDeclarationCompleted.vue';
import RightToWorkCompleted from '@/components/forms/completed/RightToWorkCompleted.vue';
import SubContractorPackCompleted from '@/components/forms/completed/SubContractorPackCompleted.vue';
import ObservationCompleted from '@/components/forms/completed/ObservationCompleted.vue';
import AccidentCompleted from '@/components/forms/completed/AccidentCompleted.vue';
import AccidentWitnessCompleted from '@/components/forms/completed/AccidentWitnessCompleted.vue';
import WorksiteInspectionCompleted from '@/components/forms/completed/WorksiteInspectionCompleted.vue';
import CossCompleted from '@/components/forms/completed/CossCompleted.vue';
import PpeRequestCompleted from '@/components/forms/completed/PpeRequestCompleted.vue';
import EmployeeHandbookSignOffCompleted from '@/components/forms/completed/EmployeeHandbookSignOffCompleted.vue';
import InvestigationCompleted from '@/components/forms/completed/InvestigationCompleted.vue';
import ClientFeedbackCompleted from '@/components/forms/completed/ClientFeedbackCompleted.vue'; // 🛡️ CLIENT FEEDBACK COMPLETED SHEET IMPORT
import SingleItemInspectionCompleted from '@/components/forms/completed/SingleItemInspectionCompleted.vue';
import MultiItemInspectionCompleted from '@/components/forms/completed/MultiItemInspectionCompleted.vue';

const route = useRoute();
const router = useRouter();
const functions = getFunctions(auth.app, 'europe-west2');
const storage = getStorage(auth.app);
const userStore = useUserStore();

const rawSubmissionData = ref(null);
const userProfile = ref(null);
const qrCodeUrl = ref('');
const loading = ref(true);
const error = ref(null);
const selectedStatus = ref('');
const originalStatus = ref('');
const isUpdatingStatus = ref(false);
const showUpdateToast = ref(false);
const copyButtonText = ref('Copy Link');
const clientLogoUrl = ref('');

const showStatusNotesModal = ref(false);
const statusChangeNotes = ref('');

const internalResponse = reactive({ providerLead: '', quoteReference: '', quoteValue: 0, quotePdfUrl: '' });
const isSubmittingReply = ref(false);
const clientDecision = reactive({ notes: '' });
const isSubmittingDecision = ref(false);
const staffDeployment = reactive({ staffName1: '', staffName2: '', staffName3: '' });
const isSubmittingDeployment = ref(false);

const submissionId = computed(() => {
    const segments = route.path.split('/');
    if (route.path.includes('/support-request/') && segments.length >= 4) return segments[3];
    return route.params.submissionId || route.params.id;
});

const collectionName = computed(() => {
    if (route.path.includes('/support-request/') || route.params.formSlug) return 'supportRequestSubmissions';
    return route.params.collectionName;
});

const isSupportRequest = computed(() => collectionName.value === 'supportRequestSubmissions');
const isPdfGenerator = computed(() => route.query.pdfToken === 'SS_PDF_GENERATOR_9922');
const submissionData = computed(() => rawSubmissionData.value ? (rawSubmissionData.value.formData ? { ...rawSubmissionData.value, ...rawSubmissionData.value.formData } : { ...rawSubmissionData.value }) : null);

const resolvedUserName = computed(() => {
  if (submissionData.value?.subjectPersonnelName) return submissionData.value.subjectPersonnelName;
  if (userProfile.value) return `${userProfile.value.firstName} ${userProfile.value.lastName}`;
  return submissionData.value?.userName || submissionData.value?.name || 'PERSONNEL';
});

const isTargetOperative = computed(() => {
    if (!auth.currentUser || !submissionData.value) return false;
    const currentUid = auth.currentUser.uid;
    return currentUid === submissionData.value.subjectPersonnelId || 
           currentUid === submissionData.value.operativeId ||
           currentUid === submissionData.value.userId;
});

const formTitles = { 
    supportRequestSubmissions: 'Support Request', 
    fatigueRiskAssessment: 'Excess Hours', 
    fatigueManagementPlan: 'Fatigue Management', 
    yourDetails: 'Onboarding', 
    ppeRequest: 'PPE Request', 
    employeeHandbookSignOff: 'Handbook Sign-Off',
    accidentWitnessStatementForm: 'Witness Statement',
    hsInvestigationRegistry: 'Incident Investigation',
    clientFeedbackForm: 'Client Feedback',
    singleItemInspectionForm: 'Single-Item Plant Inspection',
    multiItemInspectionForm: 'Multi-Item Shift Check'
};

const formIdentifier = computed(() => submissionData.value?.formIdentifier || submissionData.value?.formType || null);
const formReference = computed(() => submissionData.value?.humanReadableId || submissionData.value?.referenceNumber || 'REGISTRY');
const formTitle = computed(() => formTitles[formIdentifier.value] || formTitles[collectionName.value] || 'Audit');
const backLinkText = computed(() => userStore.isAdmin ? '← Admin' : '← Dashboard');
const goBack = () => router.go(-1);

const canManageCurrentForm = computed(() => userStore.isAdmin || userStore.permissions?.canViewEngineering || userStore.permissions?.canViewOnboarding);

// 🛡️ RE-ALIGNED: Added 'N/A / Obsolete' status option to match audit requirements
const statusOptions = computed(() => ['Pending Review', 'In Progress', 'Reviewed', 'Requires Action', 'N/A / Obsolete']);

/** 🛡️ INVESTIGATION HELPER: SKIP TO BOTTOM SUMMARY */
const skipToSummary = () => {
  const el = document.getElementById('executive-summary');
  if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
};

const fetchData = async () => {
  const cid = collectionName.value;
  const sid = submissionId.value;
  if (!sid || !cid) return;
  loading.value = true;
  try {
    const snap = await getDoc(doc(db, cid, sid));
    if (!snap.exists()) throw new Error("Record missing.");
    const data = snap.data();
    rawSubmissionData.value = data;

    if (userStore.isLoggedIn) {
        const uid = data.userId || data.formData?.userId;
        if (uid) { 
            try {
                const p = await getDoc(doc(db, 'userProfiles', uid)); 
                if (p.exists()) {
                    const profileData = p.data();
                    profileData.signatureDataUrl = profileData.signatureDataUrl || profileData.signatureUrl;
                    userProfile.value = profileData; 
                }
            } catch (pErr) { console.warn("Identity masked."); }
        }
    }
    selectedStatus.value = data.status === 'Pending' ? 'Pending Review' : (data.status === 'Completed' ? 'Reviewed' : (data.status || 'Pending Review'));
    originalStatus.value = selectedStatus.value;

    // Resolve Client ID to load custom client branding [1]
    const clientId = data.clientId || data.formData?.clientId || userStore.clientId || userStore.userProfile?.clientId;
    if (clientId) {
      try {
        const clientSnap = await getDoc(doc(db, 'clients', clientId));
        if (clientSnap.exists()) {
          const clientData = clientSnap.data();
          clientLogoUrl.value = clientData.logoUrl || clientData.clientLogoUrl || clientData.logo || clientData.clientLogo || '';
        }
      } catch (err) {
        console.warn("Client brand retrieval issue:", err);
      }
    }

    qrCodeUrl.value = await QRCode.toDataURL(window.location.href, { width: 100, margin: 1 });
  } catch (err) { 
      // 🛡️ REINFORCED ERROR HANDLER: Catches lower-cased Firebase permissions messages safely
      const msg = String(err.message || '').toLowerCase();
      error.value = (msg.includes('permission') || msg.includes('unauthorised')) ? 'Decrypting Secure Data...' : err.message; 
  } finally { loading.value = false; }
};

// 🛡️ INTERACTIVE POP-UP ACTIVATORS: Triggers non-mandatory audit notes modal instantly on selection or on submit
const handleStatusSelectionChange = () => {
  if (selectedStatus.value !== originalStatus.value) {
    showStatusNotesModal.value = true;
  }
};

const handleStatusUpdate = () => {
  showStatusNotesModal.value = true;
};

const closeStatusNotesModal = () => {
  showStatusNotesModal.value = false;
  selectedStatus.value = originalStatus.value; // Revert selected status back to original when aborted
  statusChangeNotes.value = '';
};

const confirmStatusUpdate = async () => {
  isUpdatingStatus.value = true;
  const adminName = `${userStore.userProfile.firstName} ${userStore.userProfile.lastName}`;
  try { 
    await httpsCallable(functions, 'updateFormSubmissionStatus')({ collectionName: collectionName.value, submissionId: submissionId.value, status: selectedStatus.value }); 
    const docRef = doc(db, collectionName.value, submissionId.value);
    await updateDoc(docRef, { 
      status: selectedStatus.value, 
      statusChangedBy: adminName, 
      statusChangedAt: serverTimestamp(),
      statusNotes: statusChangeNotes.value // Writes audit notes back to Firestore doc
    });
    rawSubmissionData.value.status = selectedStatus.value;
    rawSubmissionData.value.statusChangedBy = adminName;
    rawSubmissionData.value.statusChangedAt = new Date().toISOString();
    rawSubmissionData.value.statusNotes = statusChangeNotes.value;
    originalStatus.value = selectedStatus.value;
    showUpdateToast.value = true;
    closeStatusNotesModal();
    setTimeout(() => { showUpdateToast.value = false; }, 3000);
  } catch (err) { alert("Sync failed: " + err.message); } finally { isUpdatingStatus.value = false; }
};

const handleOperativeSignOff = async (signatureUrl) => {
    if (!signatureUrl) { alert("Signature Seal Required."); return; }
    isUpdatingStatus.value = true;
    try {
        const docRef = doc(db, collectionName.value, submissionId.value);
        const finalStatus = 'Pending Review';
        
        await updateDoc(docRef, { 
            status: finalStatus, 
            'formData.status': finalStatus, 
            'formData.operativeSignatureDataUrl': signatureUrl, 
            'formData.operativeAuthorisedAt': serverTimestamp() 
        });

        const submitterId = rawSubmissionData.value.userId || rawSubmissionData.value.submittedBy;
        if (submitterId) {
            await addDoc(collection(db, 'user_actions'), {
                targetUserId: submitterId,
                title: 'Handshake Completed',
                description: `Operative ${resolvedUserName.value} has counter-signed ${formTitle.value}.`,
                status: 'Pending', category: 'Compliance',
                link: `/completed-form/${collectionName.value}/${submissionId.value}`,
                createdAt: serverTimestamp(), createdBy: auth.currentUser.uid
            });
        }

        if (userStore.purgeNotificationByRelatedId) { await userStore.purgeNotificationByRelatedId(submissionId.value); }
        
        rawSubmissionData.value.status = finalStatus;
        selectedStatus.value = finalStatus;
        
        showUpdateToast.value = true;
        setTimeout(() => { 
            showUpdateToast.value = false; 
            router.push({ name: 'dashboard' }); 
        }, 2500);

    } catch (e) { 
        console.error("Handshake Registry Failure:", e);
        alert(`Handshake Error: ${e.code || 'Unauthorised'}`); 
    } finally { isUpdatingStatus.value = false; }
};

const exportToPdf = () => { window.print(); };
const copySubmissionLink = () => {
  const bespokeUrl = `${window.location.origin}/support-request/${submissionData.value?.configId || 'registry'}/${submissionId.value}`;
  navigator.clipboard.writeText(bespokeUrl);
  copyButtonText.value = 'Copied!';
  setTimeout(() => copyButtonText.value = 'Copy Link', 2500);
};

const statusClass = (s) => (s || 'Pending').toUpperCase().includes('REVIEWED') ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800';
const formatDate = (ts) => ts ? (ts.seconds ? new Date(ts.seconds * 1000) : new Date(ts)).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : 'N/A';
const formatText = (t) => t?.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) || 'N/A';
const formatBoolean = (v) => v === true ? 'Yes' : (v === false ? 'No' : 'N/A');
const formatYesNoNA = (v) => (v === true || v === 'Yes') ? 'Yes' : (v === false || v === 'No' ? 'No' : 'N/A');
const getStatusClass = (v) => (String(v).toLowerCase() === 'true' || String(v).toLowerCase() === 'yes') ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-50 text-slate-500';
const isImage = (url) => ['jpg', 'jpeg', 'png', 'gif'].includes(url?.split(/[#?]/)[0].split('.').pop().trim().toLowerCase());
const getFileIcon = () => DocumentTextIcon;
const getFileNameFromUrl = (url) => url?.split('/').pop() || 'File';

const handleQuoteFileUpload = async (event) => {
    const file = event.target.files[0];
    if (file && file.type === 'application/pdf') {
        try {
            const path = `resourceRequests/${submissionId.value}/quote_${Date.now()}.pdf`;
            const fileRef = storageRef(storage, path);
            const uploadResult = await uploadBytes(fileRef, file);
            internalResponse.quotePdfUrl = await getDownloadURL(uploadResult.ref);
        } catch (e) {
            console.error("Storage Registry Failure:", e);
            alert("File upload failure.");
        }
    }
};

const submitInternalReply = async () => {
    isSubmittingReply.value = true;
    try {
        const updateData = { 
            status: 'In Progress', 
            internalResponse: { ...internalResponse, respondedBy: userStore.userProfile.firstName, respondedAt: serverTimestamp() } 
        };
        await updateDoc(doc(db, collectionName.value, submissionId.value), updateData);
        rawSubmissionData.value.status = 'In Progress';
        rawSubmissionData.value.internalResponse = updateData.internalResponse;
    } catch (err) { console.error("Internal reply failed:", err); } finally { isSubmittingReply.value = false; }
};

const submitClientDecision = async (decision) => {
    if (!confirm(`Confirm Decision: ${decision.toUpperCase()}?`)) return;
    isSubmittingDecision.value = true;
    try {
        const updateData = { status: decision, 'formData.status': decision, clientDecision: { status: decision, notes: clientDecision.notes, decidedAt: serverTimestamp() } };
        await updateDoc(doc(db, 'supportRequestSubmissions', submissionId.value), updateData);
        rawSubmissionData.value.status = decision;
        rawSubmissionData.value.clientDecision = updateData.clientDecision;
    } catch (err) { alert("Decision failed."); } finally { isSubmittingDecision.value = false; }
};

const submitStaffDeployment = async () => {
    isSubmittingDeployment.value = true;
    try {
        const updateData = { staffDeployment: { ...staffDeployment, deployedAt: serverTimestamp(), deployedBy: userStore.userProfile.firstName } };
        await updateDoc(doc(db, 'supportRequestSubmissions', submissionId.value), updateData);
        rawSubmissionData.value.staffDeployment = updateData.staffDeployment;
    } catch (err) { console.error("Deployment failure:", err); } finally { isSubmittingDeployment.value = false; }
};

onMounted(fetchData);
watch(() => [submissionId.value, collectionName.value], fetchData);
</script>

<style scoped>
.form-input { @apply block w-full px-5 py-4 bg-white border border-slate-200 rounded-2xl text-sm font-bold text-[#0A1630] shadow-sm focus:ring-2 focus:ring-[#20FCF1] outline-none transition-all; }
.form-label { @apply block text-[10px] font-black text-[#0A1630] uppercase tracking-[0.2em] mb-2 ml-1; }
.btn-premium { @apply w-full bg-[#20FCF1] text-[#0A1630] py-5 rounded-2xl text-[10px] font-black uppercase tracking-widest shadow-2xl hover:scale-[1.02] active:scale-95 transition-all disabled:opacity-50; }
.animate-fadeIn { animation: fadeIn 0.4s ease-out forwards; }
@keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
.animate-slideDown { animation: slideDown 0.5s cubic-bezier(0.16, 1, 0.3, 1) forwards; }
@keyframes slideDown { from { opacity: 0; transform: translate(-50%, -20px); } to { opacity: 1; translate: translate(-50%, 0); } }

@media print {
  @page { size: A4 portrait; margin: 0.5cm; }
  body { background: white !important; margin: 0 !important; padding: 0 !important; }
  .max-w-7xl { max-width: 100% !important; width: 100% !important; padding: 0 !important; margin: 0 !important; }
  .printable-sheet { border: none !important; box-shadow: none !important; width: 100% !important; margin: 0 !important; padding: 0 !important; border-radius: 0 !important; opacity: 1 !important; }
  .no-print { display: none !important; }
  .printable-container { padding-top: 0 !important; margin-top: 0 !important; opacity: 1 !important; }
  * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
  img { opacity: 1 !important; }
}
</style>